<template>
  <div class="login__content-wrap">
    <SuccessMessage
      :email="email"
      title="confirmation"
      body="instructions on how to get started with your new account"
      link-to-dashboard
      v-if="submitStatus === 'done'"
    />
    <div v-if="submitStatus !== 'done'">
      <h2 class="login__section-title">
        Start with a free account, upgrade at any time.
      </h2>
      <form v-on:submit.prevent="signUp">
        <div class="input-group__field-wrap input-group__field-wrap--wide">
          <label for="firstName" class="input-group__label">First Name</label>
          <input
            id="firstName"
            class="input-group__input input-group__input--wide"
            :class="{
              'input-group__input--error':
                $v.firstName.$invalid && $v.firstName.$dirty,
            }"
            v-model="firstName"
            aria-required="true"
            :aria-invalid="$v.firstName.$invalid && $v.firstName.$dirty"
          />
          <Error
            :invalid="!$v.firstName.required && $v.firstName.$dirty"
            message="You must enter a first name"
          />
        </div>
        <div class="input-group__field-wrap input-group__field-wrap--wide">
          <label for="lastName" class="input-group__label">Last Name</label>
          <input
            id="lastName"
            class="input-group__input input-group__input--wide"
            :class="{
              'input-group__input--error':
                $v.lastName.$invalid && $v.lastName.$dirty,
            }"
            v-model="lastName"
            aria-required="true"
            :aria-invalid="$v.lastName.$invalid && $v.lastName.$dirty"
          />
          <Error
            :invalid="!$v.lastName.required && $v.lastName.$dirty"
            message="You must enter a last name"
          />
        </div>
        <div class="input-group__field-wrap input-group__field-wrap--wide">
          <label for="email" class="input-group__label">Email Address</label>
          <input
            id="email"
            class="input-group__input input-group__input--wide"
            :class="{
              'input-group__input--error': $v.email.$invalid && $v.email.$dirty,
            }"
            v-model="email"
            aria-required="true"
            :aria-invalid="$v.email.$invalid && $v.email.$dirty"
          />
          <Error
            :invalid="!$v.email.required && $v.email.$dirty"
            message="Please enter an email address"
          />
          <Error
            :invalid="$v.email.$invalid && $v.email.required && $v.email.$dirty"
            message="Please enter a valid email"
          />
        </div>
        <div class="input-group__field-wrap input-group__field-wrap--wide">
          <label for="password" class="input-group__label">Password</label>
          <input
            id="password"
            :type="hidePassword ? 'password' : 'text'"
            class="input-group__input input-group__input--wide"
            :class="{
              'input-group__input--error':
                $v.password.$invalid && $v.password.$dirty,
            }"
            v-model="password"
            aria-required="true"
            :aria-invalid="$v.password.$invalid && $v.password.$dirty"
          />
          <DisclosureButton
            :hideAction="toggleHidePassword"
            :showAction="toggleShowPassword"
            :hide="hidePassword"
            target="password"
          />
          <Error
            :invalid="!$v.password.required && $v.password.$dirty"
            message="Please enter a password"
          />
          <Error
            :invalid="!$v.password.minLength && $v.password.$dirty"
            message="Password must be at least 8 character long"
          />
        </div>
        <div class="layout--margin-bottom-md">
          <div class="layout--flex-center">
            <vue-recaptcha
              ref="recaptcha"
              @error="onError"
              @verify="onVerify"
              @expired="onExpired"
              :sitekey="siteKey"
              :loadRecaptchaScript="true"
            ></vue-recaptcha>
          </div>
          <Error
            :invalid="reCaptcha.showRequiredMessage"
            message="You must complete the reCAPTCHA to sign up."
          />
          <Error
            :invalid="reCaptcha.expired"
            message="reCAPTCHA expired. Please verify again."
          />
          <Error
            :invalid="reCaptcha.error"
            message="Error verifying reCAPTCHA. Please try again."
          />
        </div>
        <LoadingButton
          type="submit"
          wide
          big
          class="layout--margin-top-xs layout--margin-bottom-md"
          :loading="$wait.is('creating user')"
          label="Sign Up"
          loadingLabel="Signing Up ..."
          :doneLoading="submitStatus === 'done'"
          doneLoadingLabel="Sent!"
        />
        <Error :invalid="submitStatus === 'error'" :message="errorMessage" />
        <p>
          Already have an account?
          <router-link :to="{ name: 'login' }" class="link"
            >Sign in</router-link
          >
        </p>
      </form>
    </div>
  </div>
</template>

<script>
import { required, email, minLength } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import { mapWaitingActions } from "vue-wait";
import VueRecaptcha from "vue-recaptcha";

import LoadingButton from "../../components/LoadingButton";
import DisclosureButton from "../../components/DisclosureButton";
import Error from "../../components/Error";
import SuccessMessage from "./SuccessMessage";

export default {
  name: "signup-form",
  components: {
    LoadingButton,
    DisclosureButton,
    Error,
    SuccessMessage,
    VueRecaptcha,
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      submitStatus: "",
      errorMessage: "",
      hidePassword: true,
      siteKey: "6LfppAETAAAAACv4KwG0OzTSif1OBgDmmU43f3fl",
      reCaptcha: {
        verified: false,
        expired: false,
        error: false,
        showRequiredMessage: false,
      },
    };
  },
  methods: {
    ...mapWaitingActions("account", { createUser: "creating user" }),
    signUp: function() {
      this.$v.$touch();
      if (!this.reCaptcha.verified) {
        this.reCaptcha.showRequiredMessage = true;
      }
      if (this.reCaptcha.verified) {
        this.reCaptcha.showRequiredMessage = false;
      }
      if (!this.$v.$invalid && this.reCaptcha.verified) {
        const payload = {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          password: this.password,
        };
        this.createUser(payload)
          .then(() => {
            this.submitStatus = "done";
          })
          .catch(err => {
            if (
              err.response &&
              err.response.data.message === "Email already exists"
            ) {
              this.errorMessage =
                "You cannot create an account with that email address.  Please use a different email address or try <a href='/forgot-password'>resetting your password</a>.";
            } else {
              this.errorMessage =
                "Uh oh! Something went wrong. Please try again later.";
            }

            this.submitStatus = "error";
          });
      }
    },
    toggleHidePassword() {
      this.hidePassword = true;
    },
    toggleShowPassword() {
      this.hidePassword = false;
    },
    onVerify: function() {
      this.reCaptcha.verified = true;
      this.reCaptcha.expired = false;
      this.reCaptcha.error = false;
      this.reCaptcha.showRequiredMessage = false;
    },
    onExpired: function() {
      this.reCaptcha.expired = true;
      this.reCaptcha.verified = false;
    },
    onError: function() {
      this.reCaptcha.error = true;
      this.reCaptcha.verified = false;
    },
    resetRecaptcha() {
      this.$refs.recaptcha.reset(); // Direct call reset method
    },
  },
  computed: {
    ...mapGetters("session", ["error", "loggedIn"]),
  },
  validations: {
    firstName: {
      required,
    },
    lastName: {
      required,
    },
    email: {
      required,
      email,
    },
    password: {
      required,
      minLength: minLength(8),
    },
  },
};
</script>

<style></style>
